import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronLeft, ChevronRight, MessageCircle } from "lucide-react";
import { contact } from "@kanata/shared";
import { Button } from "@/components/ui/button";
import { landingApi, type HeroSlide, type LandingHero } from "@/lib/api";
import { heroImage } from "@/lib/images";
import { DEFAULT_HERO, cachedHero, isExternalHref, nextSlide, rememberHero } from "@/lib/landing-hero";
import { cn } from "@/lib/utils";

/**
 * How long to wait for the API before showing the built-in hero. The copy is
 * held back until then so it doesn't visibly change under the visitor.
 */
const SETTLE_MS = 1500;

/** A video that never reports it has ended still gives way after this. */
const VIDEO_CAP_MS = 60_000;

/** LandingHero.secondaryHref for "open Kanata's WhatsApp". Mirrors HeroWhatsApp in the API. */
const WHATSAPP = "whatsapp";

/**
 * The home page hero, authored in the admin under "Landing page": copy, two
 * buttons, and a carousel of pictures and videos behind them.
 *
 * The storefront is prerendered and there is no API at build time, so the
 * HTML carries the default copy, faded out. It fades in once the authored hero
 * arrives, or the cached one from this browser's last visit, or after
 * SETTLE_MS with the defaults.
 *
 * It is short enough that the section under it shows above the fold: a phone
 * gets the height its copy needs, a desktop about two thirds of the screen.
 * On a phone the title is smaller and the two buttons share a row, so the
 * moving review cards under it are in the first screen too (Divine, 3 Nov).
 */
/** Phones: the two buttons share a row (they wrap if a label is too long). */
const HERO_BUTTON = "h-12 flex-1 basis-0 px-4 text-[0.9375rem] sm:h-13 sm:flex-none sm:basis-auto sm:px-8 sm:text-base";

export function Hero({ authored }: { authored?: LandingHero } = {}) {
  const [hero, setHero] = useState<LandingHero | null>(authored ?? null);

  useEffect(() => {
    // A campaign page brings its own hero; there is nothing to fetch.
    if (authored) {
      setHero(authored);
      return;
    }
    let live = true;
    const cached = cachedHero();
    if (cached) setHero(cached);
    const fallback = setTimeout(() => {
      if (live) setHero((h) => h ?? DEFAULT_HERO);
    }, SETTLE_MS);
    landingApi
      .hero()
      .then((h) => {
        if (!live) return;
        setHero(h);
        rememberHero(h);
      })
      // A hero that cannot load is the built-in one.
      .catch(() => {
        if (live) setHero((h) => h ?? DEFAULT_HERO);
      })
      .finally(() => clearTimeout(fallback));
    return () => {
      live = false;
      clearTimeout(fallback);
    };
  }, [authored]);

  const pending = hero === null;
  const h = hero ?? DEFAULT_HERO;

  return (
    <section
      id="top"
      className="relative flex min-h-[440px] items-end overflow-hidden bg-ink sm:min-h-[max(520px,66svh)]"
    >
      <noscript dangerouslySetInnerHTML={{ __html: "<style>[data-hero-pending]{opacity:1!important}</style>" }} />
      {!pending &&
        (h.slides.length > 0 ? (
          // Keyed on the slides, so a fresh hero replacing the cached one
          // starts the carousel over rather than mid-way through stale slides.
          <HeroSlides key={JSON.stringify(h.slides)} slides={h.slides} intervalSeconds={h.intervalSeconds} />
        ) : (
          <img
            src={heroImage}
            alt="Standing desk Kanata di ruang kerja rumah"
            className="absolute inset-0 size-full object-cover"
            fetchPriority="high"
          />
        ))}
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-ink/85 via-ink/45 to-ink/25" />
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-ink/50 to-transparent" />

      <div
        data-hero-pending={pending ? "" : undefined}
        className={cn(
          "relative mx-auto w-full max-w-7xl px-5 pb-14 pt-20 transition-opacity duration-500 sm:px-8 sm:pb-16 sm:pt-24 lg:px-20",
          pending && "opacity-0",
        )}
      >
        <div className="max-w-3xl">
          {h.badge && (
            <span className="inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/10 px-4 py-1.5 text-xs font-medium text-white backdrop-blur-sm">
              {h.badge}
            </span>
          )}

          <h1 className="mt-4 whitespace-pre-line text-[1.75rem] font-medium leading-[1.05] tracking-tight text-white sm:mt-5 sm:text-5xl lg:text-6xl">
            {h.title}
          </h1>
          {h.subtitle && (
            <p className="mt-3 max-w-xl text-base leading-relaxed text-white/85 sm:mt-5 sm:text-xl">{h.subtitle}</p>
          )}

          <div className="mt-6 flex flex-wrap items-center gap-2.5 sm:mt-8 sm:gap-3">
            <Button asChild size="lg" className={HERO_BUTTON}>
              <HeroLink href={h.ctaHref}>
                {h.ctaLabel}
                <ArrowRight />
              </HeroLink>
            </Button>
            {h.secondaryLabel && (
              <Button asChild size="lg" variant="white" className={HERO_BUTTON}>
                {h.secondaryHref === WHATSAPP ? (
                  <a href={contact.whatsappUrl} target="_blank" rel="noopener noreferrer" data-wa-placement="hero">
                    <MessageCircle />
                    {h.secondaryLabel}
                  </a>
                ) : (
                  <HeroLink href={h.secondaryHref}>{h.secondaryLabel}</HeroLink>
                )}
              </Button>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

/**
 * A hero button's link: a storefront path stays in the app, another site
 * opens in a new tab. Spreads the props Button's asChild Slot hands down.
 */
function HeroLink({ href, children, ...rest }: { href: string; children: React.ReactNode; className?: string }) {
  return isExternalHref(href) ? (
    <a href={href} target="_blank" rel="noopener noreferrer" data-wa-placement="hero" {...rest}>
      {children}
    </a>
  ) : (
    <Link to={href} {...rest}>
      {children}
    </Link>
  );
}

/**
 * The slides, crossfading. An image stays up for intervalSeconds; a video
 * plays through once and then gives way, and loops if it is the only slide.
 * Under prefers-reduced-motion nothing moves on its own: videos show their
 * still, and the dots still change slides.
 */
function HeroSlides({ slides, intervalSeconds }: { slides: HeroSlide[]; intervalSeconds: number }) {
  const count = slides.length;
  const [active, setActive] = useState(0);
  const [reduce, setReduce] = useState(false);
  // Slides are only put in the page once they are up or next, so a visitor
  // who leaves after the first doesn't download the other seven.
  const [reached, setReached] = useState(() => new Set([0]));
  // The slide whose video the browser refused to autoplay (Low Power Mode,
  // data saver): it is timed like an image instead.
  const [blocked, setBlocked] = useState<number | null>(null);
  const videos = useRef<(HTMLVideoElement | null)[]>([]);
  const slide = slides[active];

  const advance = useCallback(() => setActive((i) => nextSlide(i, count)), [count]);

  useEffect(() => {
    setReduce(window.matchMedia("(prefers-reduced-motion: reduce)").matches);
  }, []);

  useEffect(() => {
    setReached((r) => (r.has(active) ? r : new Set(r).add(active)));
  }, [active]);

  useEffect(() => {
    if (count <= 1 || reduce || !slide) return;
    const ms = slide.kind === "image" || blocked === active ? intervalSeconds * 1000 : VIDEO_CAP_MS;
    const t = setTimeout(advance, ms);
    return () => clearTimeout(t);
  }, [active, advance, blocked, count, intervalSeconds, reduce, slide]);

  useEffect(() => {
    videos.current.forEach((v, i) => {
      if (!v) return;
      if (i === active && !reduce) {
        v.currentTime = 0;
        v.play().catch((e: unknown) => {
          // AbortError is a play() cut short by switching slides, not a refusal.
          if (e instanceof DOMException && e.name === "NotAllowedError") setBlocked(i);
        });
      } else {
        v.pause();
      }
    });
  }, [active, reduce]);

  const upNext = nextSlide(active, count);
  const previous = useCallback(() => setActive((i) => (i - 1 + count) % count), [count]);

  return (
    <>
      {slides.map((s, i) => {
        if (!reached.has(i) && i !== upNext) return null;
        const shown = i === active;
        const className = cn(
          "absolute inset-0 size-full object-cover transition-opacity duration-1000",
          shown ? "opacity-100" : "opacity-0",
        );
        return s.kind === "video" ? (
          <video
            key={i}
            ref={(el) => {
              videos.current[i] = el;
            }}
            src={s.url}
            poster={s.posterUrl || undefined}
            className={className}
            muted
            playsInline
            loop={count === 1}
            preload={shown ? "auto" : "metadata"}
            onEnded={advance}
            onError={() => shown && count > 1 && advance()}
            aria-label={s.alt || undefined}
            aria-hidden={!shown || !s.alt}
          />
        ) : (
          <img
            key={i}
            src={s.url}
            alt={shown ? s.alt : ""}
            className={className}
            fetchPriority={i === 0 ? "high" : "low"}
            aria-hidden={!shown}
          />
        );
      })}

      {count > 1 && (
        <>
          <HeroArrow side="left" onClick={previous} />
          <HeroArrow side="right" onClick={advance} />
        </>
      )}

      {count > 1 && (
        <div className="pointer-events-none absolute inset-x-0 bottom-4 z-10 mx-auto flex w-full max-w-7xl items-center justify-end gap-1 px-3 sm:bottom-6 sm:px-6">
          <PhoneArrow side="left" onClick={previous} />
          {slides.map((_, i) => (
            // The pill is thin; the button around it is a thumb-sized target.
            <button
              key={i}
              type="button"
              onClick={() => setActive(i)}
              aria-label={`Tampilkan slide ${i + 1} dari ${count}`}
              aria-current={i === active}
              className="group pointer-events-auto p-2"
            >
              <span
                className={cn(
                  "block h-1.5 rounded-full transition-all duration-300",
                  i === active ? "w-8 bg-white" : "w-3 bg-white/40 group-hover:bg-white/70",
                )}
              />
            </button>
          ))}
          <PhoneArrow side="right" onClick={advance} />
        </div>
      )}
    </>
  );
}

/**
 * A round arrow at the hero's edge, as on Omnidesk, from a tablet up. On a
 * phone the copy fills the width, so the arrows sit beside the dots instead.
 * Changing slide restarts the slide's timer, which is keyed on the active
 * slide.
 */
function HeroArrow({ side, onClick }: { side: "left" | "right"; onClick: () => void }) {
  const Icon = side === "left" ? ChevronLeft : ChevronRight;
  return (
    <Button
      type="button"
      size="icon"
      variant="white"
      onClick={onClick}
      aria-label={side === "left" ? "Slide sebelumnya" : "Slide berikutnya"}
      className={cn(
        "absolute top-1/2 z-10 hidden size-11 -translate-y-1/2 opacity-80 hover:opacity-100 sm:inline-flex",
        side === "left" ? "left-5" : "right-5",
      )}
    >
      <Icon />
    </Button>
  );
}

/** The phone's arrows, small, either side of the dots. */
function PhoneArrow({ side, onClick }: { side: "left" | "right"; onClick: () => void }) {
  const Icon = side === "left" ? ChevronLeft : ChevronRight;
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={side === "left" ? "Slide sebelumnya" : "Slide berikutnya"}
      className="pointer-events-auto grid size-8 place-items-center rounded-full bg-white/90 text-ink sm:hidden [&_svg]:size-4"
    >
      <Icon />
    </button>
  );
}
